/**
 * org-25-sample — the 25-person enterprise org chart starter, as a real
 * Seed Registry provider (P2 of the start-intent plan; replaces the
 * `loadOrgOptionsSampleData` bridge in CreateDiagramPage).
 *
 * `importOrg25Sample` is the single implementation shared by the provider
 * (deep-link / picker path) and the assistant panel's legacy
 * `loadOrgOptionsSampleData` effect, so the two paths cannot drift.
 *
 * Everything heavy is dynamically imported inside the run — the provider
 * definition itself stays out of the initial bundle.
 */

import type * as Y from 'yjs';
import type { CommandManager } from '../../../core/commands/CommandManager';
import { CompositeCommand } from '../../../core/commands/ICommand';
import type { SeedProvider } from '../../../core/seeds/SeedRegistry';
import { useOrgChartViewStore } from '../../../stores/orgchartViewStore';
import { CreateOrgPersonCommand } from '../commands/CreateOrgPersonCommand';
import { CreateReportsToCommand } from '../commands/CreateReportsToCommand';
import { ORG_PERSON_CARD_SPECS } from '../models/orgChartShapeVariants';

const SAMPLE_CSV_URL = '/sample-data/enterprise-org-25-hierarchy.csv';

/** Write `orgchart.viewMode: 'full'` into the diagram settings Y.Map —
 *  the same map `useDiagramSettings` wraps; inlined because providers
 *  can't use hooks. */
function writeFullViewMode(doc: Y.Doc | null | undefined): void {
  if (!doc) return;
  try {
    const map = doc.getMap('diagramSettings');
    const raw = map.get('settings');
    const settings = typeof raw === 'string' ? JSON.parse(raw) : {};
    settings.orgchart = { ...(settings.orgchart ?? {}), viewMode: 'full' };
    map.set('settings', JSON.stringify(settings));
  } catch (error) {
    console.warn('[org25SampleSeed] failed to write viewMode', error);
  }
}

function writeManualDrawSettings(doc: Y.Doc | null | undefined): void {
  if (!doc) return;
  try {
    const map = doc.getMap('diagramSettings');
    const raw = map.get('settings');
    const settings = typeof raw === 'string' ? JSON.parse(raw) : {};
    settings.orgchart = {
      ...(settings.orgchart ?? {}),
      viewMode: 'compact',
      autoLayoutEnabled: false,
    };
    map.set('settings', JSON.stringify(settings));
  } catch (error) {
    console.warn('[orgManualStarterSeed] failed to write settings', error);
  }
}

export async function importOrgManualStarter(
  cm: CommandManager,
  doc?: Y.Doc | null,
): Promise<boolean> {
  const cardDimensions = ORG_PERSON_CARD_SPECS.compact.card;
  const centerX = 520;
  const topY = 140;
  const levelGap = 145;
  const siblingGap = 280;

  const ceo = new CreateOrgPersonCommand({
    x: centerX,
    y: topY,
    width: cardDimensions.width,
    height: cardDimensions.height,
    name: 'Your CEO',
    role: 'Chief Executive Officer',
    shapeType: 'text',
    accentColor: '#2563EB',
  });
  const operations = new CreateOrgPersonCommand({
    x: centerX - siblingGap,
    y: topY + levelGap,
    width: cardDimensions.width,
    height: cardDimensions.height,
    name: 'Operations Lead',
    role: 'Head of Operations',
    shapeType: 'text',
    accentColor: '#10B981',
  });
  const sales = new CreateOrgPersonCommand({
    x: centerX,
    y: topY + levelGap,
    width: cardDimensions.width,
    height: cardDimensions.height,
    name: 'Sales Lead',
    role: 'Head of Sales',
    shapeType: 'text',
    accentColor: '#0EA5E9',
  });
  const product = new CreateOrgPersonCommand({
    x: centerX + siblingGap,
    y: topY + levelGap,
    width: cardDimensions.width,
    height: cardDimensions.height,
    name: 'Product Lead',
    role: 'Head of Product',
    shapeType: 'text',
    accentColor: '#F59E0B',
  });
  const operationsMember = new CreateOrgPersonCommand({
    x: centerX - siblingGap,
    y: topY + levelGap * 2,
    width: cardDimensions.width,
    height: cardDimensions.height,
    name: 'Operations Teammate',
    role: 'Team Member',
    shapeType: 'text',
    accentColor: '#10B981',
  });
  const salesMember = new CreateOrgPersonCommand({
    x: centerX,
    y: topY + levelGap * 2,
    width: cardDimensions.width,
    height: cardDimensions.height,
    name: 'Sales Teammate',
    role: 'Team Member',
    shapeType: 'text',
    accentColor: '#0EA5E9',
  });
  const productMember = new CreateOrgPersonCommand({
    x: centerX + siblingGap,
    y: topY + levelGap * 2,
    width: cardDimensions.width,
    height: cardDimensions.height,
    name: 'Product Teammate',
    role: 'Team Member',
    shapeType: 'text',
    accentColor: '#F59E0B',
  });

  await cm.execute(new CompositeCommand('Create Manual Org Starter', [
    ceo,
    operations,
    sales,
    product,
    operationsMember,
    salesMember,
    productMember,
    new CreateReportsToCommand({
      fromId: operations.getCreatedId(),
      toId: ceo.getCreatedId(),
    }),
    new CreateReportsToCommand({
      fromId: sales.getCreatedId(),
      toId: ceo.getCreatedId(),
    }),
    new CreateReportsToCommand({
      fromId: product.getCreatedId(),
      toId: ceo.getCreatedId(),
    }),
    new CreateReportsToCommand({
      fromId: operationsMember.getCreatedId(),
      toId: operations.getCreatedId(),
    }),
    new CreateReportsToCommand({
      fromId: salesMember.getCreatedId(),
      toId: sales.getCreatedId(),
    }),
    new CreateReportsToCommand({
      fromId: productMember.getCreatedId(),
      toId: product.getCreatedId(),
    }),
  ]));

  const viewStore = useOrgChartViewStore.getState();
  viewStore.setAutoLayoutEnabled(false);
  viewStore.setLastUsedOrgPersonNodeType('org_person_text');
  viewStore.requestViewportFit();
  writeManualDrawSettings(doc);
  cm.clear();
  return true;
}

export async function importOrg25Sample(
  cm: CommandManager,
  doc?: Y.Doc | null,
): Promise<boolean> {
  const [
    { parseCsvFile },
    { buildOrgChartCsvGraph },
    { orgChartHydrator },
    { ORG_PERSON_CARD_SPECS },
    { computeAutoCollapseIds, applyOrgChartCollapse },
    { OrgChartImportCommand },
    { LayoutOrgChartCommand },
    { computeFullTreeLayout },
    { useDiagramStore },
    { useOrgChartViewStore },
  ] = await Promise.all([
    import('@/lib/csv/parseCsv'),
    import('../interop/OrgChartCsvBuilder'),
    import('../interop/OrgChartHydrator'),
    import('../models/orgChartShapeVariants'),
    import('../services/orgChartCollapseProjection'),
    import('../commands/OrgChartImportCommand'),
    import('../commands/LayoutOrgChartCommand'),
    import('../services/graphAnalysis'),
    import('../../../stores/diagramStore'),
    import('../../../stores/orgchartViewStore'),
  ]);

  const response = await fetch(SAMPLE_CSV_URL);
  if (!response.ok) return false;
  const csvText = await response.text();

  const file = new File([new Blob([csvText], { type: 'text/csv' })], 'enterprise-org-25-hierarchy.csv', {
    type: 'text/csv',
  });
  const { rows } = await parseCsvFile(file);
  if (rows.length === 0) return false;

  const graph = buildOrgChartCsvGraph(rows, {
    columns: {
      personId: 'personId',
      name: 'name',
      title: 'title',
      department: 'department',
      reportsTo: 'reportsTo',
      photoRef: 'photoRef',
    },
    additionalFieldIds: [],
  });
  if (graph.nodes.length === 0) return false;

  const scene = orgChartHydrator.fromUniversal(graph);
  const targetDims = ORG_PERSON_CARD_SPECS.full.card.image;
  const importedNodes = scene.nodes.map((node) => {
    if (node.type !== 'org_person') return node;
    return {
      ...node,
      type: 'org_person_photo',
      width: targetDims.width,
      height: targetDims.height,
      w: targetDims.width,
      h: targetDims.height,
    };
  });

  const toCollapse = computeAutoCollapseIds(importedNodes, scene.edges, 150);
  if (toCollapse.length > 0) {
    useOrgChartViewStore.getState().setCollapsedNodeIds(toCollapse);
  }

  await cm.execute(new OrgChartImportCommand(importedNodes, scene.edges));
  writeFullViewMode(doc);

  const latestNodes = useDiagramStore.getState().nodes;
  const latestEdges = useDiagramStore.getState().edges;
  const collapsedIds = useOrgChartViewStore.getState().collapsedNodeIds;
  const collapseResult =
    collapsedIds.length > 0 ? applyOrgChartCollapse(latestNodes, latestEdges, collapsedIds) : null;
  const layoutNodes = collapseResult?.nodes ?? latestNodes;
  const layoutEdges = collapseResult?.edges ?? latestEdges;
  const positions = computeFullTreeLayout(layoutNodes, layoutEdges);
  if (positions.size > 0) {
    await cm.execute(new LayoutOrgChartCommand({ positions, edges: latestEdges, nodes: layoutNodes }));
  }

  useOrgChartViewStore.getState().requestViewportFit();
  // A starter is the canvas's initial state — undoing back to blank is
  // never what the user means. Same call the panel's import path makes.
  cm.clear();
  return true;
}

export const org25SampleSeed: SeedProvider = {
  id: 'org-25-sample',
  type: 'org',
  label: '25-person sample org',
  provide: () => ({
    format: 'commands',
    build: async (cm, ctx) => {
      await importOrg25Sample(cm, ctx?.doc);
    },
  }),
};

export const orgManualStarterSeed: SeedProvider = {
  id: 'org-manual-starter',
  type: 'org',
  label: 'Manual org chart starter',
  provide: () => ({
    format: 'commands',
    build: async (cm, ctx) => {
      await importOrgManualStarter(cm, ctx?.doc);
    },
  }),
};

/** Trigger-shaped variant (starter-registry plan R4): same sample org,
 *  framed as a reorg scenario — the urgency moment is the reorg
 *  announcement; the artifact is an ordinary org chart to restructure. */
export const reorgPlanSeed: SeedProvider = {
  id: 'reorg-plan',
  type: 'org',
  label: 'Reorg plan (scenario)',
  provide: () => ({
    format: 'commands',
    build: async (cm, ctx) => {
      await importOrg25Sample(cm, ctx?.doc);
    },
  }),
};
